// src/pages/AllOrders.jsx → FINAL ESLINT CLEAN + SHIPROCKET AUTOMATED

import { useEffect, useState } from "react";
import {
  Package,
  Search,
  ExternalLink,
  Truck,
  RefreshCw,
  XCircle,
} from "lucide-react";

export default function AllOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const ADMIN_TOKEN = "7a8b9c0d1e2f3g4h5i6j7k8l9m0n1o2p3q4r5s6t";

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/orders/admin/", {
        headers: {
          Authorization: `${ADMIN_TOKEN}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setOrders(data);
    } catch (err) {
      console.error(err);
      alert("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId) => {
    // ESLINT SAFE CONFIRM
    if (!window.confirm("Cancel this order in Shiprocket too?")) return;

    try {
      const res = await fetch(`/api/orders/${orderId}/cancel/`, {
        method: "POST",
        headers: {
          Authorization: `${ADMIN_TOKEN}`,
          "Content-Type": "application/json",
        },
      });
      if (res.ok) {
      alert("Order cancelled successfully!");
        fetchOrders();
      } else {
        const error = await res.json();
        alert(error.error || "Cancellation failed");
      }
    } catch {
      alert("Network error");
    }
  };

  const filteredOrders = orders.filter((order) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      order.order_number.toLowerCase().includes(searchLower) ||
      (order.username && order.username.toLowerCase().includes(searchLower)) ||
      (order.awb_code && order.awb_code.toLowerCase().includes(searchLower));

    const matchesFilter = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status, hasAwb) => {
    const styles = {
      pending: "bg-gray-100 text-gray-800",
      processing: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      rto: "bg-orange-100 text-orange-800",
    };
    const base = styles[status] || styles.pending;
    const ring = hasAwb ? "ring-2 ring-green-500 ring-offset-2" : "";
    return `px-4 py-2 rounded-full text-sm font-bold ${base} ${ring}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl flex items-center gap-3">
          <RefreshCw className="animate-spin w-8 h-8" />
          Syncing with Shiprocket...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <h1 className="text-4xl font-bold flex items-center gap-4">
                <Truck className="w-12 h-12" />
                All Orders ({orders.length})
              </h1>
              <button
                onClick={fetchOrders}
                className="px-6 py-3 bg-white text-green-600 rounded-xl font-bold hover:bg-gray-100 flex items-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Refresh
              </button>
            </div>
            <p className="mt-2 text-green-100">Auto-sync every 30 seconds</p>
          </div>

          {/* SEARCH & FILTER */}
          <div className="p-6 border-b">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Order#, Name, AWB..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-6 py-3 border rounded-xl bg-white"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4 font-bold text-gray-700">Order #</th>
                  <th className="text-left p-4 font-bold text-gray-700">Customer</th>
                  <th className="text-left p-4 font-bold text-gray-700">Total</th>
                  <th className="text-left p-4 font-bold text-gray-700">AWB / Courier</th>
                  <th className="text-left p-4 font-bold text-gray-700">Status</th>
                  <th className="text-left p-4 font-bold text-gray-700">Date</th>
                  <th className="text-left p-4 font-bold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-20">
                      <Package className="w-20 h-20 mx-auto mb-4 text-gray-300" />
                      <p className="text-xl text-gray-500">No orders found</p>
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="p-4 font-mono font-bold text-green-600">
                        {order.order_number}
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{order.username || "Guest"}</p>
                          <p className="text-sm text-gray-500">{order.address?.phone}</p>
                        </div>
                      </td>
                      <td className="p-4 font-bold text-lg">₹{order.grand_total}</td>
                      <td className="p-4">
                        {order.awb_code ? (
                          <div>
                            <p className="font-mono text-blue-600 font-bold">{order.awb_code}</p>
                            <p className="text-sm text-gray-600">{order.courier_name || "Auto"}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400">AWB Pending</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={getStatusBadge(order.status, order.awb_code)}>
                          {order.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleString()}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          {order.tracking_url && (
                            <a
                              href={order.tracking_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              Track <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          {order.status !== "cancelled" && order.status !== "delivered" && !order.awb_code && (
                            <button
                              onClick={() => cancelOrder(order.id)}
                              className="text-red-600 hover:text-red-800 flex items-center gap-1"
                            >
                              <XCircle className="w-4 h-4" />
                              Cancel
                            </button>
                          )}
                          <a
                            href={`/invoice/${order.id}`}
                            target="_blank"
                            className="text-green-600 hover:underline"
                          >
                            Invoice
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 text-center text-sm text-gray-500 border-t">
            Live sync with Shiprocket • Auto-refresh every 30 seconds
          </div>
        </div>
      </div>
    </div>
  );
}